import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Custom Bed — per Quoting_markdown/beds.md. Headboard Only and Entire Bed
// share this one form (Entire Bed just reveals extra fields/sections) since
// both quote the same headboard. Submitted through dedicated
// addBedDraftQuoteObject/updateBedDraftQuoteObject mutations (see
// DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject), same
// deviation Box Spring Cover/Bench/etc. already established.

// Standard mattress dimensions (Length × Width, inches) — a convenience
// auto-fill for Mattress Length/Width on Entire Bed, same "auto-fill until
// the user overrides it by hand" pattern as Traversing Drape's Rod Face.
// Not read by calcBed() itself; the shop can always type custom dimensions.
const MATTRESS_SIZES: Record<string, { length: number; width: number }> = {
  twin: { length: 75, width: 38 },
  full: { length: 75, width: 54 },
  queen: { length: 80, width: 60 },
  king: { length: 80, width: 76 },
};

const BedForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [bedConfig, setBedConfig] = useState<'headboard_only' | 'entire_bed' | ''>((iv?.bedConfig as any) || '');
  const [headboardStyle, setHeadboardStyle] = useState<'standard' | 'tufted' | 'channeled' | ''>((iv?.headboardStyle as any) || '');
  const [headboardShaped, setHeadboardShaped] = useState<boolean>(iv?.headboardShaped || false);
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidthInches, setFabricWidthInches] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeatInches, setPatternRepeatInches] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');

  const [bedEdgeTreatment, setBedEdgeTreatment] = useState<'none' | 'trim' | 'self_welt' | ''>((iv?.bedEdgeTreatment as any) || 'none');
  const [trimTop, setTrimTop] = useState<boolean>(iv?.trimTop || false);
  const [trimSides, setTrimSides] = useState<boolean>(iv?.trimSides || false);
  const [trimFace, setTrimFace] = useState<boolean>(iv?.trimFace || false);

  // Entire Bed only.
  const [mattressSize, setMattressSize] = useState<string>('');
  const [mattressLengthInches, setMattressLengthInches] = useState(iv?.mattressLengthInches != null ? String(iv.mattressLengthInches) : '');
  const [mattressWidthInches, setMattressWidthInches] = useState(iv?.mattressWidthInches != null ? String(iv.mattressWidthInches) : '');
  const [frameThicknessInches, setFrameThicknessInches] = useState(iv?.frameThicknessInches != null ? String(iv.frameThicknessInches) : '');
  const [sideRailFootboardHeightInches, setSideRailFootboardHeightInches] = useState(iv?.sideRailFootboardHeightInches != null ? String(iv.sideRailFootboardHeightInches) : '');
  const [sideRailFootboardStyleDescription, setSideRailFootboardStyleDescription] = useState<string>(iv?.sideRailFootboardStyleDescription || '');
  const [bedDrawersSmallQty, setBedDrawersSmallQty] = useState(iv?.bedDrawersSmallQty != null ? String(iv.bedDrawersSmallQty) : '0');
  const [bedDrawersLargeQty, setBedDrawersLargeQty] = useState(iv?.bedDrawersLargeQty != null ? String(iv.bedDrawersLargeQty) : '0');
  const [bedTrundle, setBedTrundle] = useState<boolean>(iv?.bedTrundle || false);
  const [bedFeetType, setBedFeetType] = useState<'none' | 'wood' | 'specialty' | ''>((iv?.bedFeetType as any) || 'none');
  const [bedFeetHeightInches, setBedFeetHeightInches] = useState(iv?.bedFeetHeightInches != null ? String(iv.bedFeetHeightInches) : '');
  const [bedFeetFinish, setBedFeetFinish] = useState<string>(iv?.bedFeetFinish || '');
  const [bedFeetSpecialtyDescription, setBedFeetSpecialtyDescription] = useState<string>(iv?.bedFeetSpecialtyDescription || '');
  const [bedFeetSpecialtyPrice, setBedFeetSpecialtyPrice] = useState(iv?.bedFeetSpecialtyPriceCents != null ? String(iv.bedFeetSpecialtyPriceCents / 100) : '');

  // Either bed_config.
  const [bedBunkyBoards, setBedBunkyBoards] = useState<boolean>(iv?.bedBunkyBoards || false);
  const [bedBunkyBoardWidthInches, setBedBunkyBoardWidthInches] = useState(iv?.bedBunkyBoardWidthInches != null ? String(iv.bedBunkyBoardWidthInches) : '');
  const [includeMeasureCharge, setIncludeMeasureCharge] = useState<boolean>(iv?.includeMeasureCharge || false);

  const [error, setError] = useState<string | null>(null);

  const isEntireBed = bedConfig === 'entire_bed';

  // Mattress Size auto-fills Length/Width, same "auto-fill until the user
  // overrides it" pattern as Traversing Drape's Rod Face.
  useEffect(() => {
    const preset = MATTRESS_SIZES[mattressSize];
    if (!preset) return;
    setMattressLengthInches(String(preset.length));
    setMattressWidthInches(String(preset.width));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mattressSize]);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!bedConfig) { setError('Bed Config is required'); return null; }
      if (!headboardStyle) { setError('Headboard Style is required'); return null; }
      if (!width) { setError('Headboard Width is required'); return null; }
      if (!height) { setError('Headboard Height is required'); return null; }
      if (isEntireBed && !mattressLengthInches) { setError('Mattress Length is required for Entire Bed'); return null; }
      if (isEntireBed && !mattressWidthInches) { setError('Mattress Width is required for Entire Bed'); return null; }
      setError(null);
      return {
        bedConfig,
        headboardStyle,
        headboardShaped,
        widthInches: parseFloat(width),
        heightInches: parseFloat(height),
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidthInches ? parseFloat(fabricWidthInches) : 54,
        patternRepeatInches: patternRepeatInches ? parseFloat(patternRepeatInches) : null,
        bedEdgeTreatment: bedEdgeTreatment || 'none',
        trimTop: bedEdgeTreatment !== 'none' ? trimTop : false,
        trimSides: bedEdgeTreatment !== 'none' ? trimSides : false,
        trimFace: bedEdgeTreatment !== 'none' ? trimFace : false,
        mattressLengthInches: isEntireBed && mattressLengthInches ? parseFloat(mattressLengthInches) : null,
        mattressWidthInches: isEntireBed && mattressWidthInches ? parseFloat(mattressWidthInches) : null,
        frameThicknessInches: isEntireBed && frameThicknessInches ? parseFloat(frameThicknessInches) : null,
        sideRailFootboardHeightInches: isEntireBed && sideRailFootboardHeightInches ? parseFloat(sideRailFootboardHeightInches) : null,
        sideRailFootboardStyleDescription: isEntireBed ? (sideRailFootboardStyleDescription.trim() || null) : null,
        bedDrawersSmallQty: isEntireBed ? (parseInt(bedDrawersSmallQty, 10) || 0) : 0,
        bedDrawersLargeQty: isEntireBed ? (parseInt(bedDrawersLargeQty, 10) || 0) : 0,
        bedTrundle: isEntireBed ? bedTrundle : false,
        bedFeetType: isEntireBed ? (bedFeetType || 'none') : 'none',
        bedFeetHeightInches: isEntireBed && bedFeetType === 'wood' && bedFeetHeightInches ? parseFloat(bedFeetHeightInches) : null,
        bedFeetFinish: isEntireBed && bedFeetType === 'wood' ? (bedFeetFinish.trim() || null) : null,
        bedFeetSpecialtyDescription: isEntireBed && bedFeetType === 'specialty' ? (bedFeetSpecialtyDescription.trim() || null) : null,
        bedFeetSpecialtyPriceCents: isEntireBed && bedFeetType === 'specialty' && bedFeetSpecialtyPrice ? Math.round(parseFloat(bedFeetSpecialtyPrice) * 100) : null,
        bedBunkyBoards,
        bedBunkyBoardWidthInches: bedBunkyBoards && bedBunkyBoardWidthInches ? parseFloat(bedBunkyBoardWidthInches) : null,
        includeMeasureCharge,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bed Config</label>
          <select
            className="input"
            value={bedConfig}
            onChange={(e) => setBedConfig(e.target.value as 'headboard_only' | 'entire_bed' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="headboard_only">Headboard Only</option>
            <option value="entire_bed">Entire Bed</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Headboard Style</label>
          <select
            className="input"
            value={headboardStyle}
            onChange={(e) => setHeadboardStyle(e.target.value as 'standard' | 'tufted' | 'channeled' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="standard">Standard</option>
            <option value="tufted">Tufted</option>
            <option value="channeled">Channeled</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={headboardShaped}
              onChange={(e) => setHeadboardShaped(e.target.checked)} disabled={props.disabled} />
            {' '}Shaped headboard (+$5/in)
          </label>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Headboard Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={width}
            onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Headboard Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={height}
            onChange={(e) => setHeight(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Width (inches)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidthInches}
            onChange={(e) => setFabricWidthInches(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeatInches}
            onChange={(e) => setPatternRepeatInches(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Edge Treatment (headboard)</label>
          <select
            className="input"
            value={bedEdgeTreatment}
            onChange={(e) => setBedEdgeTreatment(e.target.value as 'none' | 'trim' | 'self_welt' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="trim">Trim</option>
            <option value="self_welt">Self Welt</option>
          </select>
        </div>
        {bedEdgeTreatment !== 'none' && (
          <>
            <div className="form-group" style={{ flex: 1 }}>
              <label>
                <input type="checkbox" checked={trimTop}
                  onChange={(e) => setTrimTop(e.target.checked)} disabled={props.disabled} />
                {' '}Top
              </label>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>
                <input type="checkbox" checked={trimSides}
                  onChange={(e) => setTrimSides(e.target.checked)} disabled={props.disabled} />
                {' '}Sides
              </label>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>
                <input type="checkbox" checked={trimFace}
                  onChange={(e) => setTrimFace(e.target.checked)} disabled={props.disabled} />
                {' '}Face
              </label>
            </div>
          </>
        )}
      </div>

      {isEntireBed && (
        <>
          <div className="add-item-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Mattress Size <span style={{ color: '#9ca3af', fontWeight: 400 }}>(auto-fills Length/Width)</span></label>
              <select className="input" value={mattressSize} onChange={(e) => setMattressSize(e.target.value)} disabled={props.disabled}>
                <option value="">Select...</option>
                <option value="twin">Twin</option>
                <option value="full">Full</option>
                <option value="queen">Queen</option>
                <option value="king">King</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Mattress Length (inches)</label>
              <input type="number" min="0" step="0.25" className="input" value={mattressLengthInches}
                onChange={(e) => setMattressLengthInches(e.target.value)} disabled={props.disabled} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Mattress Width (inches)</label>
              <input type="number" min="0" step="0.25" className="input" value={mattressWidthInches}
                onChange={(e) => setMattressWidthInches(e.target.value)} disabled={props.disabled} />
            </div>
          </div>

          <div className="add-item-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Frame Thickness (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(side rails & footboard)</span></label>
              <input type="number" min="0" step="0.25" className="input" value={frameThicknessInches}
                onChange={(e) => setFrameThicknessInches(e.target.value)} disabled={props.disabled} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Side Rail / Footboard Height (inches)</label>
              <input type="number" min="0" step="0.25" className="input" value={sideRailFootboardHeightInches}
                onChange={(e) => setSideRailFootboardHeightInches(e.target.value)} disabled={props.disabled} />
            </div>
          </div>

          <div className="add-item-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Side Rail / Footboard Style <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shown on quote and work order)</span></label>
              <input type="text" className="input" value={sideRailFootboardStyleDescription}
                onChange={(e) => setSideRailFootboardStyleDescription(e.target.value)} disabled={props.disabled} />
            </div>
          </div>

          <div className="add-item-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label># Drawers (≤36" wide)</label>
              <input type="number" min="0" step="1" className="input" value={bedDrawersSmallQty}
                onChange={(e) => setBedDrawersSmallQty(e.target.value)} disabled={props.disabled} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label># Drawers (&gt;36" wide)</label>
              <input type="number" min="0" step="1" className="input" value={bedDrawersLargeQty}
                onChange={(e) => setBedDrawersLargeQty(e.target.value)} disabled={props.disabled} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>
                <input type="checkbox" checked={bedTrundle}
                  onChange={(e) => setBedTrundle(e.target.checked)} disabled={props.disabled} />
                {' '}Trundle (+$980 flat)
              </label>
            </div>
          </div>

          <div className="add-item-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Feet</label>
              <select className="input" value={bedFeetType} onChange={(e) => setBedFeetType(e.target.value as 'none' | 'wood' | 'specialty' | '')} disabled={props.disabled}>
                <option value="none">None</option>
                <option value="wood">Wood</option>
                <option value="specialty">Specialty</option>
              </select>
            </div>
            {bedFeetType === 'wood' && (
              <>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Foot Height (inches)</label>
                  <input type="number" min="0" step="0.25" className="input" value={bedFeetHeightInches}
                    onChange={(e) => setBedFeetHeightInches(e.target.value)} disabled={props.disabled} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Finish</label>
                  <input type="text" className="input" value={bedFeetFinish}
                    onChange={(e) => setBedFeetFinish(e.target.value)} disabled={props.disabled} />
                </div>
              </>
            )}
            {bedFeetType === 'specialty' && (
              <>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Specialty Feet Description</label>
                  <input type="text" className="input" value={bedFeetSpecialtyDescription}
                    onChange={(e) => setBedFeetSpecialtyDescription(e.target.value)} disabled={props.disabled} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Specialty Feet Price (set of 4, total)</label>
                  <input type="number" min="0" step="0.01" className="input" value={bedFeetSpecialtyPrice}
                    onChange={(e) => setBedFeetSpecialtyPrice(e.target.value)} disabled={props.disabled} />
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={bedBunkyBoards}
              onChange={(e) => setBedBunkyBoards(e.target.checked)} disabled={props.disabled} />
            {' '}Bunky Boards
          </label>
        </div>
        {bedBunkyBoards && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Bunky Board Width (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={bedBunkyBoardWidthInches}
              onChange={(e) => setBedBunkyBoardWidthInches(e.target.value)} disabled={props.disabled} />
          </div>
        )}
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={includeMeasureCharge}
              onChange={(e) => setIncludeMeasureCharge(e.target.checked)} disabled={props.disabled} />
            {' '}Include Measure Charge
          </label>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default BedForm;
